
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ZXingScanner } from '../../components/ZXingScanner';
import { API } from '../../utils/api';

export function MobPaySettings({ headers, user }) {
  const [scanningBinding, setScanningBinding] = useState(false);
  const [boundParents, setBoundParents] = useState([]);
  const [pendingBind, setPendingBind] = useState(null);
  const [buddyType, setBuddyType] = useState(user?.buddy_type || 'minor');
  const [relationship, setRelationship] = useState('');
  const [legalConsent, setLegalConsent] = useState(false);

  const fetchBoundParents = async () => {
    try {
      const res = await axios.get(`${API}/mobpay/all-parents`, { headers });
      setBoundParents(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch bound parents:', e);
      setBoundParents([]);
    }
  };

  const onScanBinding = async (result) => {
    try {
      const txt = result?.getText?.() || '';
      const data = JSON.parse(txt);
      if (data.type === 'bind_parent' || data.type === 'bind_payer') {
        setScanningBinding(false);
        const parentId = data.parent_id || data.payer_id;
        setPendingBind({ parent_id: parentId, code: data.code });
        toast.success('Binding details captured. Please confirm.');
      } else {
        toast.error('Invalid binding QR');
      }
    } catch (e) {
      setScanningBinding(false);
      toast.error('Scan failed');
    }
  };

  useEffect(() => { fetchBoundParents(); }, []);

  return (
    <div className="space-y-8" data-testid="mobpay-settings">
      <div>
        <h2 className="text-xl font-semibold mb-4" data-testid="buddy-binding-title">Binding</h2>
        <Card data-testid="buddy-binding-card">
          <CardHeader>
            <CardTitle className="text-base">Scan Binding QR Code</CardTitle>
            <CardDescription>Scan a Payer's binding QR code to connect</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button data-testid="scan-binding-qr" onClick={()=>setScanningBinding(true)}>Scan Binding QR</Button>
            {scanningBinding && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <ZXingScanner onScan={onScanBinding} onClose={()=>setScanningBinding(false)} />
              </div>
            )}
            {pendingBind && (
              <div className="space-y-3" data-testid="binding-details-form">
                <div>
                  <label className="text-sm">Buddy Type</label>
                  <Select value={buddyType} onValueChange={setBuddyType}>
                    <SelectTrigger data-testid="buddy-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Minor (under 18)</SelectItem>
                      <SelectItem value="buddy">Buddy (18+)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm">Relationship</label>
                  <Input
                    value={relationship}
                    onChange={(e) => setRelationship(e.target.value)}
                    placeholder="e.g. Child / Sibling"
                    data-testid="relationship-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={legalConsent} onCheckedChange={setLegalConsent} data-testid="legal-consent-checkbox" />
                  <span className="text-sm">I have legal consent to bind this Buddy</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="confirm-binding-button"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/mobpay/bind`, {
                          parent_id: pendingBind.parent_id,
                          code: pendingBind.code,
                          buddy_type: buddyType,
                          relationship,
                          legal_consent: legalConsent
                        }, { headers });
                        toast.success('Binding success');
                        setPendingBind(null);
                        fetchBoundParents();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Binding failed');
                      }
                    }}
                  >
                    Confirm Binding
                  </Button>
                  <Button variant="outline" data-testid="cancel-binding-button" onClick={() => setPendingBind(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3" data-testid="buddy-bound-payers-title">Bound Payers</h3>
        {boundParents.length === 0 && <p className="text-sm text-muted-foreground" data-testid="buddy-no-bound-payers">No bound payers yet</p>}
        <div className="space-y-2">
          {boundParents.map(p => (
            <Card key={p.id} className={p.binding_enabled === false ? 'border-red-300 bg-red-50' : ''} data-testid={`bound-payer-${p.id}`}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  {p.profile_icon && <img src={p.profile_icon} alt="" className="w-10 h-10 rounded-full" />}
                  <div>
                    <div className="font-medium">{p.full_name}</div>
                    <div className="text-xs text-muted-foreground">{p.email}</div>
                    {p.binding_enabled === false && (
                      <div className="text-xs text-red-600 mt-1" data-testid={`payer-disabled-${p.id}`}>⚠️ Disabled by payer - cannot send requests</div>
                    )}
                  </div>
                </div>
                <div>
                  {p.binding_enabled === false ? (
                    <Badge variant="destructive" data-testid={`payer-status-${p.id}`}>Inactive</Badge>
                  ) : (
                    <Badge variant="default" data-testid={`payer-status-${p.id}`}>Active</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
